import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Typography, TextField, Button, CircularProgress, IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import { GetCompanyInfo, UpdateCompanyInfo } from "api/salesDocument";

const emptyAccount = () => ({ accountNumber: "", currency: "THB" });

// Letterhead + bank account details used on generated Receipt/Quotation/Invoice
// PDFs (see scenes/salesDocuments). Stored as a single AppSetting("company.info")
// document on the backend — see controllers/salesDocument.js.
const CompanyInfoSettings = () => {
  const { t } = useTranslation();
  const [info, setInfo] = useState(null); // null = loading
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    GetCompanyInfo().then((res) => {
      if (res.success) {
        setInfo(res.data || { nameEN: "", address: "", tel: "", email: "", website: "", authorizedSignerName: "", bankAccountHolder: "", bankAccounts: [] });
      } else {
        setInfo({ nameEN: "", address: "", tel: "", email: "", website: "", authorizedSignerName: "", bankAccountHolder: "", bankAccounts: [] });
        Swal.fire({ icon: "error", text: res.message || "Failed to load company info", timer: 2500, toast: true, position: "top-right", showConfirmButton: false });
      }
    });
  }, []);

  const setField = (field, value) => setInfo((prev) => ({ ...prev, [field]: value }));
  const updateAccount = (idx, field, value) => setInfo((prev) => ({
    ...prev,
    bankAccounts: prev.bankAccounts.map((a, i) => (i === idx ? { ...a, [field]: value } : a)),
  }));
  const addAccount = () => setInfo((prev) => ({ ...prev, bankAccounts: [...(prev.bankAccounts || []), emptyAccount()] }));
  const removeAccount = (idx) => setInfo((prev) => ({ ...prev, bankAccounts: prev.bankAccounts.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    const res = await UpdateCompanyInfo(info);
    setSaving(false);
    Swal.fire({
      icon: res.success ? "success" : "error",
      text: res.success ? t("settings.companyInfo.saved") : res.message || "Save failed",
      timer: 2000, toast: true, position: "top-right", showConfirmButton: false,
    });
  };

  if (info === null) {
    return <Box sx={{ p: 3 }}><CircularProgress color="secondary" /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 640, py: 2 }}>
      <Typography variant="h6" color="secondary" sx={{ mb: 2 }}>{t("settings.companyInfo.title")}</Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "16px", mb: "16px" }}>
        <TextField label={t("settings.companyInfo.nameEN")} value={info.nameEN || ""} onChange={(e) => setField("nameEN", e.target.value)} />
        <TextField label={t("settings.companyInfo.authorizedSignerName")} value={info.authorizedSignerName || ""} onChange={(e) => setField("authorizedSignerName", e.target.value)} />
        <TextField label={t("settings.companyInfo.tel")} value={info.tel || ""} onChange={(e) => setField("tel", e.target.value)} />
        <TextField label={t("settings.companyInfo.email")} value={info.email || ""} onChange={(e) => setField("email", e.target.value)} />
        <TextField label={t("settings.companyInfo.website")} value={info.website || ""} onChange={(e) => setField("website", e.target.value)} />
      </Box>

      <TextField
        fullWidth multiline minRows={2} sx={{ mb: "20px" }}
        label={t("settings.companyInfo.address")}
        value={info.address || ""}
        onChange={(e) => setField("address", e.target.value)}
      />

      <TextField
        fullWidth sx={{ mb: "16px" }}
        label={t("settings.companyInfo.bankAccountHolder")}
        value={info.bankAccountHolder || ""}
        onChange={(e) => setField("bankAccountHolder", e.target.value)}
        helperText={t("settings.companyInfo.bankAccountHolderHelp")}
      />

      <Typography variant="subtitle2" color="secondary" sx={{ mb: 1 }}>{t("settings.companyInfo.bankAccounts")}</Typography>
      {(info.bankAccounts || []).map((account, idx) => (
        <Box key={idx} sx={{ display: "grid", gridTemplateColumns: "3fr 1fr auto", gap: "10px", mb: "10px", alignItems: "center" }}>
          <TextField size="small" label={t("settings.companyInfo.accountNumber")} value={account.accountNumber} onChange={(e) => updateAccount(idx, "accountNumber", e.target.value)} />
          <TextField
            size="small" select label={t("salesDocuments.currency")} value={account.currency}
            onChange={(e) => updateAccount(idx, "currency", e.target.value)}
            SelectProps={{ native: true }}
          >
            <option value="THB">THB</option>
            <option value="LAK">LAK</option>
            <option value="USD">USD</option>
          </TextField>
          <IconButton size="small" onClick={() => removeAccount(idx)}><DeleteOutlineIcon fontSize="small" /></IconButton>
        </Box>
      ))}
      <Button variant="outlined" color="secondary" size="small" startIcon={<AddIcon />} onClick={addAccount} sx={{ mb: "24px" }}>
        {t("settings.companyInfo.addBankAccount")}
      </Button>

      <Box>
        <Button variant="contained" color="secondary" onClick={save} disabled={saving}>
          {saving ? <CircularProgress size={20} color="inherit" /> : t("settings.companyInfo.save")}
        </Button>
      </Box>
    </Box>
  );
};

export default CompanyInfoSettings;
